import React, {
	useCallback,
	useEffect,
	useMemo,
	useReducer,
	useRef,
	useState,
	type ComponentType,
} from 'react';
import { styled } from '@compiled/react';
// eslint-disable-next-line @atlaskit/design-system/no-unsupported-drag-and-drop-libraries
import type {
	BeforeCapture,
	DraggableProvided,
	DraggableRubric,
	DraggableStateSnapshot,
	DropResult,
	DroppableStateSnapshot,
} from 'react-beautiful-dnd';
import { lazyForPaint } from 'react-loosely-lazy';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { getPresenceNodeId, useCollabStore } from '@atlassian/jira-business-collaboration';
import {
	GROUP_BY_STATUS,
	INLINE_CREATE_PARAM,
	SELECTED_ISSUE_PARAM,
} from '@atlassian/jira-business-constants';
import { useProject } from '@atlassian/jira-business-entity-project-hook';
import { ErrorPage } from '@atlassian/jira-business-error-page';
import { ExperienceFailed } from '@atlassian/jira-business-experience-tracking';
import { isAutomationDiscoveryOnBoardColumnsExperimentEnabled } from '@atlassian/jira-business-feature-flags';
import { ThemedFilterEmptyState, useSimpleSearchJQL } from '@atlassian/jira-business-filters';
import {
	ModalIssueApp,
	SidebarIssueView,
	useIssueAppSwitcher,
} from '@atlassian/jira-business-issue-view';
import { PrefetchIssueDataController } from '@atlassian/jira-business-issue-view-critical-data-loader';
import { BoardEmbedOnboarding } from '@atlassian/jira-business-onboarding-board-embed';
import { PerformanceAnalytics, RenderStartMark } from '@atlassian/jira-business-performance';
import { FILTER_PREFERENCE, useViewPreference } from '@atlassian/jira-business-preferences';
import { SignupInviteModal } from '@atlassian/jira-business-signup-invite-modal';
import { JWMSpaStatePageReady } from '@atlassian/jira-business-spa-state-page-ready';
import { ThemedSpinner } from '@atlassian/jira-business-theme-components';
import { BUSINESS_BOARD } from '@atlassian/jira-common-constants/src/analytics-sources';
import { ErrorBoundary } from '@atlassian/jira-error-boundary';
import { ff } from '@atlassian/jira-feature-flagging';
import { ValidationError } from '@atlassian/jira-fetch';
import { useFlagsService } from '@atlassian/jira-flags';
import { useViewMode, VIEW_MODE_SIDEBAR } from '@atlassian/jira-issue-context-service';
import type { OnChangeCallback } from '@atlassian/jira-issue-view-model/src/change-type';
import {
	AutomationDiscoveryProvider,
	useJwmAutomationDiscoveryOnBoardColumnsExperiment,
} from '@atlassian/jira-moon-experiment-automation-discovery-on-board-columns';
import Placeholder from '@atlassian/jira-placeholder';
import {
	type CardId,
	type ColumnId,
	type ColumnCreateProps,
	COLUMN_DND_TYPE,
} from '@atlassian/jira-platform-board-kit';
import { BoardDragDropContext, Board } from '@atlassian/jira-platform-board-kit-legacy-dnd';
import { usePreviousWithInitial } from '@atlassian/jira-platform-react-hooks-use-previous';
import { fireTrackAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useQueryParam } from '@atlassian/jira-router';
import { toIssueKey } from '@atlassian/jira-shared-types';
import UFOSegment from '@atlassian/jira-ufo-segment';
import UFOLoadHold from '@atlassian/ufo-load-hold';
import usePressTracing from '@atlassian/ufo-use-press-tracing';
import {
	BOARD_VIEW_COLUMNS_OFFSET,
	COLUMN_HEADER_HEIGHT,
	InvalidMove,
	VIEW_EXPERIENCE,
} from '../../common/constants';
import type { BoardColumn, BoardIssueOld } from '../../common/types';
import { validateColumnName } from '../../common/utils/validate-column-name';
import { ActionsMenuPopupProvider } from '../../controllers/actions-menu-popup-context-old';
import { useAddColumn } from '../../controllers/add-rename-delete-status-old';
import { useBoardController } from '../../controllers/board-controller-old';
import { useInitializeBoard } from '../../controllers/board-data-old';
import { BoardIssueDetailsOld } from '../../controllers/board-issue-details-old';
import { useBoardSubtasks } from '../../controllers/board-subtasks-old';
import BoardViewUpdater from '../../controllers/board-view-updater-old';
import { useCardAnimationSensor } from '../../controllers/card-animation-old';
import { useCardTransitionsOld } from '../../controllers/card-transitions-old';
import { useConfettiColumnActions } from '../../controllers/column-confetti-old';
import { matchConfettiEmoji } from '../../controllers/column-confetti-old/utils';
import { useColumnMoveHandlerOld } from '../../controllers/column-move-handler-old';
import { useJWMBoardFeatures } from '../../controllers/features-context';
import { useGroupBy } from '../../controllers/group-by';
import IssueActionsContext from '../../controllers/issue-actions-context-old';
import { useScrollElementStore } from '../../controllers/scroll-element-old';
import { useBoardFilters } from '../../services/board-filters-old';
import { useAreAnyColumnsUpdating, useBoardStore } from '../../services/board-store';
// eslint-disable-next-line jira/import/no-parent-imports
import messages from '../board/messages';
import ColumnDeleteModal from './column-delete-modal';
import JWMBoardColumn from './column-old';
import DeleteIssueModal from './delete-issue-modal';
import { EmptyBoardComponent } from './empty-state';
import { Minimap } from './minimap';
import { DraggingColumn, LoadingWrapper } from './styled';
import VirtualColumns from './virtual-columns';

// TODO: JET-1882 Remove these overrides. Extend components API instead.

// moved it to a separate hook for testing
export const useColumnsHeight = () => {
	const [columnsHeight, setColumnsHeight] = useState<number | undefined>();
	return [columnsHeight, setColumnsHeight] as const;
};
export const useColumnsRef = () => useRef<HTMLDivElement | null>(null);
export const useScrollRef = () => useRef<HTMLElement | null>(null);

// extracted method for testing
export const findDraggingColumnIndex = (
	// eslint-disable-next-line @typescript-eslint/no-invalid-void-type
	snapshot: DroppableStateSnapshot | void,
	columns: BoardColumn[],
) => {
	const columnId = snapshot?.draggingFromThisWith?.split('COLUMN::')[1];
	return columns.findIndex(
		(col) => columnId != null && (col.id === columnId || col.id === Number(columnId)),
	);
};

export const AsyncColumnCreate = lazyForPaint<ComponentType<ColumnCreateProps>>(
	() => import(/* webpackChunkName: "async-jwm-board-create-column" */ './column-create'),
);

// eslint-disable-next-line complexity
const JWMBoard = () => {
	const [issueToDelete, setIssueToDelete] = useState<BoardIssueOld | null>(null);
	const resetIssueToDelete = useCallback(() => setIssueToDelete(null), []);
	const { featureView, isOnboardingEnabled } = useJWMBoardFeatures();
	const { showFlag } = useFlagsService();
	const { isSearching } = useSimpleSearchJQL();
	const projectData = useProject();
	const projectId = String(projectData.id);
	const isTeamManaged = projectData.isSimplified;
	const deleteIssueAllowed = projectData.permissions.deleteIssues;

	const [, { sendNodeSelected, sendClearNodeSelection }] = useCollabStore();

	useBoardSubtasks();
	const { isFiltering, isLoading: isLoadingFilters } = useBoardFilters();
	const [{ columnToDelete }, { setColumnToDelete, updateIssue }] = useBoardStore();
	const [filtersPreference] = useViewPreference(FILTER_PREFERENCE);
	const { addConfettiColumn } = useConfettiColumnActions();

	const { shouldRenderExperiment: automationDiscoveryEnabled } =
		isAutomationDiscoveryOnBoardColumnsExperimentEnabled()
			? // eslint-disable-next-line react-hooks/rules-of-hooks
				useJwmAutomationDiscoveryOnBoardColumnsExperiment(true)
			: { shouldRenderExperiment: false };

	// Making sure Filter values query doesn't block page rendering when filter is not applied
	const isLoadingFiltersState = Boolean(filtersPreference) && isLoadingFilters;

	const { columns, issues, onRemoveIssue } = useBoardController();

	const { loading, error } = useInitializeBoard();

	const [initialPageLoading, setInitialpageLoading] = useState(loading || isLoadingFiltersState);

	useEffect(() => {
		if (initialPageLoading && !loading && !isLoadingFiltersState) {
			setInitialpageLoading(false);
		}
	}, [initialPageLoading, isLoadingFiltersState, loading]);

	const [groupBy] = useGroupBy();

	const ufoPressTracing = usePressTracing('jwm.board.issue-view-opened');

	const [columnsHeight, setColumnsHeight] = useColumnsHeight();
	const { onDragStart, onDragEnd, onCardsMove, onDragUpdate } = useCardTransitionsOld();
	const [areAnyColumnsUpdating] = useAreAnyColumnsUpdating();
	const { onColumnMove } = useColumnMoveHandlerOld();
	const [selectedIssueKey, setSelectedIssueKey] = useQueryParam(SELECTED_ISSUE_PARAM);
	const [inlineCreateParam] = useQueryParam(INLINE_CREATE_PARAM);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const prevSelectedIssueKey = usePreviousWithInitial(selectedIssueKey);
	const [{ openInSidebar }] = useIssueAppSwitcher();
	// eslint-disable-next-line react-hooks/rules-of-hooks
	const viewMode = ff('refactor-bento-view-mode-logic') ? useViewMode() : null;
	const isViewModeSideBar = ff('refactor-bento-view-mode-logic')
		? viewMode === VIEW_MODE_SIDEBAR
		: openInSidebar;

	const shouldShowEmbedOnboarding =
		!isFiltering &&
		inlineCreateParam == null &&
		selectedIssueKey == null &&
		isOnboardingEnabled &&
		issues.length >= 3;

	const onIssueClick = useCallback(
		(issue: BoardIssueOld, withCmd: boolean) => {
			if (issue.id && issue.key) {
				if (withCmd) {
					window.open(`/browse/${issue.key}`, '_blank');
				} else {
					ufoPressTracing();
					setSelectedIssueKey(issue.key);

					const nodeId = getPresenceNodeId({
						projectId,
						viewId: 'board',
						nodeId: `${issue.id}`,
					});
					sendNodeSelected({ nodeId });
				}
			}
		},
		[projectId, sendNodeSelected, setSelectedIssueKey, ufoPressTracing],
	);

	const [, forceUpdate] = useReducer((x) => x + 1, 0);
	const optimisticColumnMove = useCallback(
		// @ts-expect-error - TS7019 - Rest parameter 'args' implicitly has an 'any[]' type.
		(...args) => {
			// Something is causing react to bail out immediately after column move updates states.
			// This is temporary until we revisit this again on JET-2387.
			// @ts-expect-error - TS2556 - A spread argument must either have a tuple type or be passed to a rest parameter.
			onColumnMove(...args);
			forceUpdate();
		},
		[onColumnMove],
	);

	const optimisticCardMove = useCallback(
		(
			cardIds: CardId[],
			sourceColumnId: ColumnId,
			destinationColumnId: ColumnId,
			sourceCardIndex: number,
			destinationCardIndex: number,
		) => {
			onCardsMove(
				cardIds,
				sourceColumnId,
				destinationColumnId,
				sourceCardIndex,
				destinationCardIndex,
			)
				.then(() => {
					const eligibleConfettiDoneColumns = columns.filter(
						(column) => column.isDone && !column.isCollapsed,
					);
					const eligibleConfettiEmojiColumns = columns.filter(
						(column) => matchConfettiEmoji(column.name) && !column.isCollapsed,
					);
					const isMovedToDone = eligibleConfettiDoneColumns.some(
						(column) => column.id === destinationColumnId,
					);
					const wasPreviouslyDone = eligibleConfettiDoneColumns.some(
						(column) => column.id === sourceColumnId,
					);
					const isMovedToEmojiColumn = eligibleConfettiEmojiColumns.some(
						(column) => column.id === destinationColumnId,
					);
					const isNewlyDoneIssue = isMovedToDone && !wasPreviouslyDone;

					if (isNewlyDoneIssue || isMovedToEmojiColumn) {
						addConfettiColumn(Number(destinationColumnId));

						fireTrackAnalytics(createAnalyticsEvent({}), 'boardConfetti created', {
							columnType: isNewlyDoneIssue ? 'done' : 'emoji',
							columnId: destinationColumnId,
						});
					}
				})
				.catch((e) => {
					// do not show an error for an invalid move, it has not sent a request to the backend
					// or if the error is undefined, it means that the move was canceled
					if (!e || e instanceof InvalidMove) {
						return;
					}

					const errorMessage = e instanceof ValidationError ? e.message : null;

					showFlag({
						type: 'error',
						title: messages.itemMoveErrorTitle,
						description: errorMessage ?? messages.itemMoveErrorMessage,
					});
				});

			forceUpdate();
		},
		[onCardsMove, showFlag, columns, addConfettiColumn, createAnalyticsEvent],
	);

	const columnHasNoIssues = columns.filter((column) => column.issues.length > 0).length === 0;

	const isBoardEmptyAfterFilter =
		columnHasNoIssues && (isFiltering || isSearching) && !initialPageLoading;
	const isBoardEmpty = columnHasNoIssues && !isFiltering && !initialPageLoading;

	const onIssueModalClose = useCallback(() => {
		setSelectedIssueKey(undefined);
	}, [setSelectedIssueKey]);

	useEffect(() => {
		// when issueKey param is removed from URL we need to clear node selection
		if (selectedIssueKey === undefined && prevSelectedIssueKey != null) {
			const issue = issues.find((item) => item.key === prevSelectedIssueKey);
			if (issue) {
				const nodeId = getPresenceNodeId({
					projectId,
					viewId: 'board',
					nodeId: `${issue.id}`,
				});
				sendClearNodeSelection({ nodeId });
			}
		}
	}, [
		issues,
		prevSelectedIssueKey,
		projectId,
		selectedIssueKey,
		sendClearNodeSelection,
		sendNodeSelected,
	]);

	const onDeleteSuccess = useCallback(
		({ issueId }: { issueId: string | number }) => onRemoveIssue(Number(issueId)),
		[onRemoveIssue],
	);

	const onBentoChange: OnChangeCallback = useCallback(
		(event) => updateIssue(Number(event.issueId), { needsExtendedFieldsUpdate: true }),
		[updateIssue],
	);

	// Memoised to prevent flicker on JIRT updates
	const issueModal = useMemo(
		() =>
			selectedIssueKey !== undefined && (
				<ModalIssueApp
					issueKey={toIssueKey(selectedIssueKey)}
					onClose={onIssueModalClose}
					analyticsSource={BUSINESS_BOARD}
					onDeleteSuccess={onDeleteSuccess}
					onChange={onBentoChange}
				/>
			),
		[selectedIssueKey, onIssueModalClose, onDeleteSuccess, onBentoChange],
	);

	const scrollRef = useScrollRef();
	const columnsRef = useColumnsRef();
	const [, { setScrollElement }] = useScrollElementStore();

	const issueActions = useMemo(
		() => ({
			onIssueClick,
			onIssueDelete: setIssueToDelete,
			isDeleteIssueAllowed: deleteIssueAllowed,
		}),
		[deleteIssueAllowed, onIssueClick],
	);

	const columnRenderer = useCallback(
		// @ts-expect-error - TS7031 - Binding element 'column' implicitly has an 'any' type. | TS7031 - Binding element 'columnIndex' implicitly has an 'any' type. | TS7031 - Binding element 'boardSize' implicitly has an 'any' type. | TS7031 - Binding element 'isVirtualized' implicitly has an 'any' type.
		({ column, columnIndex, boardSize, isVirtualized }) => (
			<JWMBoardColumn
				key={column.name}
				column={column}
				columnIndex={columnIndex}
				scrollRef={scrollRef}
				isVirtualized={isVirtualized}
				boardSize={boardSize}
			/>
		),
		[scrollRef],
	);

	const renderDraggableColumn = useCallback(
		(provided: DraggableProvided, snapshot: DraggableStateSnapshot, rubric: DraggableRubric) => {
			const column = columns[rubric.source.index];
			if (!column || !scrollRef.current) {
				return null;
			}

			return (
				<DraggingColumn
					ref={provided.innerRef}
					{...provided.draggableProps}
					{...provided.dragHandleProps}
				>
					<JWMBoardColumn
						isClone
						key={column.name}
						column={column}
						columnIndex={rubric.source.index}
						scrollRef={scrollRef}
						isDragging={snapshot.isDragging}
					/>
				</DraggingColumn>
			);
		},
		[columns, scrollRef],
	);

	const getDraggingColumnIndex = useCallback(
		(snapshot?: DroppableStateSnapshot) => findDraggingColumnIndex(snapshot, columns),
		[columns],
	);

	const onDragEndFn = useCallback(
		(dragStop?: DropResult) => {
			onDragEnd(dragStop);
			setColumnsHeight(undefined);
		},
		[onDragEnd, setColumnsHeight],
	);

	const onBeforeCaptureFn = useCallback(
		(beforeCapture?: BeforeCapture) => {
			if (beforeCapture?.draggableId.startsWith(COLUMN_DND_TYPE) && columnsRef.current != null) {
				setColumnsHeight(columnsRef.current.offsetHeight);

				if (scrollRef.current) {
					scrollRef.current.scrollTop = 0;
				}
			}
		},
		[columnsRef, scrollRef, setColumnsHeight],
	);

	const onColumnCreateValidate = useCallback(
		(_: unknown, name: string) => validateColumnName(columns, name),
		[columns],
	);
	const { addNewColumn } = useAddColumn();
	const onColumnCreateSubmit = useCallback(
		(_: unknown, columnName: string) => {
			addNewColumn({ columnName });
		},
		[addNewColumn],
	);

	const handleScrollRef = useCallback(
		// @ts-expect-error - TS7006 - Parameter 'ref' implicitly has an 'any' type.
		(ref) => {
			scrollRef.current = ref;
			setScrollElement(ref);
		},
		[scrollRef, setScrollElement],
	);

	if (error != null) {
		return (
			<>
				<ExperienceFailed experience={VIEW_EXPERIENCE} error={error} />
				<ErrorPage error={error} />
			</>
		);
	}

	const BoardContent = (
		<Board
			dropZoneMode="virtual"
			noBottomPadding
			offsetLeftPaddingByWidth
			offsetRightPaddingByWidth
			// @ts-expect-error - TS2769 - No overload matches this call.
			renderClone={renderDraggableColumn}
			scrollRef={handleScrollRef}
		>
			{(placeHolder, snapshot) => (
				<IssueActionsContext.Provider value={issueActions}>
					<VirtualColumns
						scrollRef={scrollRef}
						columns={columns}
						columnRenderer={columnRenderer}
						draggingColumnIndex={getDraggingColumnIndex(snapshot)}
						columnsHeight={columnsHeight}
						columnsRef={columnsRef}
					/>
					{isTeamManaged && (!groupBy || groupBy === GROUP_BY_STATUS) && (
						<ErrorBoundary
							id="async.jira-business.board.column-create"
							packageName="async.jira-business.board.column-create"
						>
							<Placeholder name="column-create" fallback={null}>
								<AsyncColumnCreate
									isDisabled={areAnyColumnsUpdating}
									validate={onColumnCreateValidate}
									onSubmit={onColumnCreateSubmit}
								/>
							</Placeholder>
						</ErrorBoundary>
					)}
				</IssueActionsContext.Provider>
			)}
		</Board>
	);

	return (
		<Container>
			<>
				{initialPageLoading ? (
					<UFOLoadHold name="board-container-initial">
						<LoadingWrapper>
							<ThemedSpinner />
						</LoadingWrapper>
					</UFOLoadHold>
				) : (
					<UFOSegment name="business-board-content-old">
						<BoardContainer isBoardEmpty={isBoardEmpty || isBoardEmptyAfterFilter}>
							<RenderStartMark view={featureView} />
							<BoardDragDropContext
								onColumnMove={optimisticColumnMove}
								onCardsMove={optimisticCardMove}
								onDragStart={onDragStart}
								onDragEnd={onDragEndFn}
								onDragUpdate={onDragUpdate}
								onBeforeCapture={onBeforeCaptureFn}
								sensors={[useCardAnimationSensor]}
							>
								<BoardWrapper featureView={featureView}>
									<ActionsMenuPopupProvider>
										{automationDiscoveryEnabled ? (
											<AutomationDiscoveryProvider columns={columns}>
												{BoardContent}
											</AutomationDiscoveryProvider>
										) : (
											BoardContent
										)}
									</ActionsMenuPopupProvider>
									<Minimap />
								</BoardWrapper>
							</BoardDragDropContext>
							<JWMSpaStatePageReady />
						</BoardContainer>
					</UFOSegment>
				)}

				{isBoardEmpty ? <EmptyBoardComponent /> : null}
				{isBoardEmptyAfterFilter ? <ThemedFilterEmptyState /> : null}

				<BoardViewUpdater />
				<BoardIssueDetailsOld />
				<DeleteIssueModal issue={issueToDelete} onClose={resetIssueToDelete} />
				{columnToDelete && (
					<ColumnDeleteModal
						column={columnToDelete}
						onColumnDeleteModalClose={() => setColumnToDelete(null)}
						columns={columns}
					/>
				)}
				{isViewModeSideBar ? (
					<SidebarIssueView
						onChange={onBentoChange}
						issueKey={selectedIssueKey}
						onClose={onIssueModalClose}
						analyticsSource={BUSINESS_BOARD}
						onIssueDeleteSuccess={onDeleteSuccess}
					/>
				) : (
					issueModal
				)}
				{shouldShowEmbedOnboarding && <BoardEmbedOnboarding />}
				<PerformanceAnalytics
					view={featureView}
					loading={initialPageLoading}
					itemsCount={issues.length}
				/>
				<SignupInviteModal />
				{ff('jwm.preload-issue-view-critical-data') && <PrefetchIssueDataController />}
			</>
		</Container>
	);
};

export default JWMBoard;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Container = styled.div({
	flex: 1,
	minHeight: 0,
	display: 'flex',
	flexDirection: 'column',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const BoardContainer = styled.div<{ isBoardEmpty?: boolean }>({
	display: 'flex',
	flexDirection: 'column',
	flexWrap: 'nowrap',
	height: '100%',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	maxHeight: ({ isBoardEmpty }) => (isBoardEmpty ? '204px' : 'none'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const BoardWrapper = styled.div<{ featureView: 'jwm-board' | 'jwm-board-embed' }>({
	width: '100%',
	flex: 1,
	minHeight: 0,
	minWidth: 0,
	position: 'relative',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text', colors.N800),
	/* Remove the positive left margin from the board so that it aligns with the header actions and top navigation */
	marginLeft: token('space.negative.075', '-6px'),

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'section > div:last-child': {
		visibility: 'hidden',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'section > div:first-of-type': {
		visibility: 'hidden',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'section > div, [data-component-selector="platform-board-kit.ui.column.column-transition-zones-container.inner-container"]':
		{
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
			maxHeight: ({ featureView }) => `min(
                  100%,
${/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766 */ ''}
                  calc(100vh - ${BOARD_VIEW_COLUMNS_OFFSET[featureView] - 4}px)
              ) `,
		},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'[data-component-selector="platform-board-kit.ui.column.column-transition-zones-container.outer-transition-container"]':
		{
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
			maxHeight: `calc(100% - ${COLUMN_HEADER_HEIGHT}px)`,
		},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'[data-component-selector="platform-board-kit.ui.column.column-transition-zones-container.transition-to-zone.transition-to-zone"]':
		{
			top: token('space.050', '4px'),
		},
});
